import { useReveal } from '../hooks/useReveal.js';

type Group = {
  emblem: string; // icon slug under /icons/saint-<slug>-sq
  emblemAlt: string;
  eyebrow: string;
  title: string;
  features: string[];
};

// A full inventory of what Orthodox Way actually does today, drawn from the
// app's real modules (prayer, liturgical engine, services, scripture, saints,
// formation, parish) rather than a marketing shortlist.
const groups: Group[] = [
  {
    emblem: 'christ-in-glory',
    emblemAlt: 'Icon of Christ in glory',
    eyebrow: 'The inner life',
    title: 'Prayer',
    features: [
      'Morning, evening & compline prayers',
      'Pre- and post-Communion prayers',
      'The Midday Rule - the Sixth Hour',
      'The Jesus Prayer rope, 33, 50 or 100 knots',
      'The Jordanville Prayer Book, 194 voiced prayers',
      'Pray-along audio, read in a reverent voice',
      'Build your own rule from your father’s blessing',
      'A private, encrypted reflection journal',
    ],
  },
  {
    emblem: 'theotokos-sign',
    emblemAlt: 'Icon of the Theotokos of the Sign',
    eyebrow: 'The Church’s calendar',
    title: 'The Liturgical Day',
    features: [
      'A liturgical engine: Pascha, the eight tones, the four fasts',
      'New Calendar and Old (Julian) Calendar',
      'The day’s feast and its rank',
      'Today - one quiet digest for the morning',
      'Fasting guidance at three levels, without guilt',
      'Fasting recipes for the season',
      'Sync the liturgical calendar to Google',
    ],
  },
  {
    emblem: 'crucifixion',
    emblemAlt: 'Icon of the Crucifixion',
    eyebrow: 'In the temple',
    title: 'The Services',
    features: [
      'Vespers, Matins & the Divine Liturgy',
      'Compline, the Akathist, Panikhida & Molieben',
      'Today’s variable hymns resolved automatically',
      'Simple mode for the pew, full text for the chanter',
      'Pray-along service audio',
    ],
  },
  {
    emblem: 'all-saints',
    emblemAlt: 'Icon of All Saints',
    eyebrow: 'The word & the witnesses',
    title: 'Scripture & Saints',
    features: [
      'The day’s Epistle and Gospel',
      'Guided Bible reading plans',
      'Lives of the saints, 366 daily commemorations',
      'Troparia, icons & name-day greetings',
      'Sayings of the Fathers, a new card each day',
    ],
  },
  {
    emblem: 'john-climacus',
    emblemAlt: 'Icon of St John of the Ladder',
    eyebrow: 'Coming to the faith',
    title: 'Formation & Learning',
    features: [
      'The Catechumen’s Journey - a guided, three-stage path',
      'Ask a priest your questions',
      'The Orthodox Faith library, 364 articles',
      'A curated library of talks & homilies',
      'Short, clergy-reviewed lessons',
    ],
  },
  {
    emblem: 'hierarch-enthroned',
    emblemAlt: 'Icon of a hierarch enthroned',
    eyebrow: 'Your community',
    title: 'Parish & Community',
    features: [
      'Find a canonical parish near you',
      'Service schedules & weekly bulletins',
      'Submit names for commemoration',
      'Book confession & appointments',
      'A full admin surface for parish stewards',
      'Giving - coming soon',
    ],
  },
];

const everywhere: string[] = [
  'Web, Android & iOS',
  'A home-screen widget',
  'Reverent notifications - no streaks, no nudges',
  'Works offline, on device',
  'Sign in with Google',
  'Light & dark, brand-true',
];

function GroupCard({ group, delay }: { group: Group; delay: number }) {
  const ref = useReveal<HTMLDivElement>(0.15);
  const base = `/icons/saint-${group.emblem}-sq`;
  return (
    <div
      ref={ref}
      className="ow-reveal ow-cap-card bg-bg-elevated border border-border rounded-card flex flex-col h-full"
      style={
        {
          padding: 30,
          ['--reveal-delay' as never]: `${delay}ms`,
        } as React.CSSProperties
      }
    >
      <div className="flex items-center gap-4">
        <span className="ow-cap-emblem">
          <picture>
            <source srcSet={`${base}.webp`} type="image/webp" />
            <img
              src={`${base}.jpg`}
              alt={group.emblemAlt}
              loading="lazy"
              decoding="async"
              width={120}
              height={120}
            />
          </picture>
        </span>
        <div>
          <p
            className="font-display uppercase text-gold"
            style={{ fontSize: 10, letterSpacing: '0.28em' }}
          >
            {group.eyebrow}
          </p>
          <h3
            className="font-display text-primary mt-1"
            style={{ fontSize: 22, fontWeight: 500, letterSpacing: '0.02em' }}
          >
            {group.title}
          </h3>
        </div>
      </div>

      <ul className="ow-cap-list mt-6 flex flex-col gap-3">
        {group.features.map((f) => (
          <li key={f} className="flex items-start gap-3">
            <span className="ow-cap-cross" aria-hidden>
              ✛
            </span>
            <span className="text-secondary" style={{ fontSize: 14.5, lineHeight: 1.55 }}>
              {f}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Capabilities() {
  const headerRef = useReveal<HTMLDivElement>(0.3);
  const footRef = useReveal<HTMLDivElement>(0.2);
  const totalFeatures = groups.reduce((n, g) => n + g.features.length, 0) + everywhere.length;

  return (
    <section
      id="capabilities"
      className="relative w-full"
      style={{ padding: '76px 20px', background: 'var(--ow-bg-subtle)' }}
    >
      <div className="mx-auto max-w-container">
        <div ref={headerRef} className="ow-reveal text-center max-w-[820px] mx-auto">
          <p
            className="font-display uppercase text-gold"
            style={{ fontSize: 11, letterSpacing: '0.32em' }}
          >
            Everything inside
          </p>
          <h2
            className="font-display text-primary mt-5"
            style={{
              fontSize: 'clamp(32px, 5vw, 56px)',
              fontWeight: 500,
              letterSpacing: '0.02em',
            }}
          >
            The whole Orthodox life, in one place.
          </h2>
          <p className="scripture text-secondary mt-4" style={{ fontSize: 21, lineHeight: 1.5 }}>
            {totalFeatures}+ tools for prayer, fasting, the services, the Scriptures, the saints,
            and your parish - woven into a single quiet day.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-16">
          {groups.map((g, i) => (
            <GroupCard key={g.title} group={g} delay={(i % 2) * 120} />
          ))}
        </div>

        {/* Across every device - a wide closing band */}
        <div
          ref={footRef}
          className="ow-reveal ow-cap-everywhere mt-6 bg-bg-elevated border border-border rounded-card"
          style={{ padding: 30 }}
        >
          <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10">
            <div className="lg:w-[260px] shrink-0">
              <p
                className="font-display uppercase text-gold"
                style={{ fontSize: 10, letterSpacing: '0.28em' }}
              >
                Wherever you are
              </p>
              <h3
                className="font-display text-primary mt-1"
                style={{ fontSize: 22, fontWeight: 500, letterSpacing: '0.02em' }}
              >
                On every device
              </h3>
            </div>
            <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 flex-1">
              {everywhere.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <span className="ow-cap-cross" aria-hidden>
                    ✛
                  </span>
                  <span className="text-secondary" style={{ fontSize: 14.5, lineHeight: 1.5 }}>
                    {f}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
